import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import customsClient, { setCustomsToken } from "@/lib/customsApi";
import { ShipmentsOverview, ShipmentRow, stColor } from "@/components/shipmentsShared";
import { TrackingPanel } from "@/pages/Vessels";
import { Button } from "@/components/ui/button";
import { Ship, LogOut, ArrowLeft, Loader2, FileText, Container, Users, Search, MessageSquarePlus, CheckCircle2, HelpCircle, Info } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const LOGO = "/tilesmate-logo.png";

const NOTE_META = { Cleared: { color: "#059669", icon: CheckCircle2 }, Query: { color: "#B4791A", icon: HelpCircle }, Info: { color: "#0891B2", icon: Info } };

function Shell({ me, children }) {
  const logout = async () => {
    try { await customsClient.post("/customs/auth/logout"); } catch { /* noop */ }
    setCustomsToken(null);
    window.location.assign("/customs");
  };
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="border-b-[3px] border-[#F5A623] bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="TilesMate" className="h-9 w-auto" />
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-bold text-primary"><Ship className="h-3 w-3" />Customs Portal</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-muted-foreground sm:inline">{me?.name || me?.email}</span>
            <Button variant="outline" size="sm" onClick={logout} className="gap-1.5" data-testid="customs-logout-btn"><LogOut className="h-3.5 w-3.5" />Sign out</Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-8">{children}</main>
    </div>
  );
}

function DetailView({ vid, navigate }) {
  const { data: s, isLoading } = useQuery({ queryKey: ["customs-ship", vid], queryFn: async () => (await customsClient.get(`/customs/shipments/${vid}`)).data, retry: false });
  const qc = useQueryClient();
  const [noteStatus, setNoteStatus] = useState("Query");
  const [noteText, setNoteText] = useState("");
  const [posting, setPosting] = useState(false);
  const addNote = async () => {
    if (!noteText.trim()) { toast.error("Add a note first"); return; }
    setPosting(true);
    try { await customsClient.post(`/customs/shipments/${vid}/note`, { status: noteStatus, text: noteText }); toast.success("Note sent to the team"); setNoteText(""); qc.invalidateQueries({ queryKey: ["customs-ship", vid] }); }
    catch { toast.error("Couldn't add note"); } finally { setPosting(false); }
  };
  const viewDoc = async (id) => {
    try {
      const r = await customsClient.get(`/customs/shipments/${vid}/doc/${id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Couldn't open document"); }
  };
  if (isLoading) return <div className="h-64 animate-pulse rounded-2xl bg-muted/50" />;
  if (!s) return <div className="py-20 text-center text-muted-foreground">Shipment not found.</div>;
  const isFcx = s.ship_type === "FCX";
  const title = isFcx ? (s.vessel_name || s.hbl || "Consolidated shipment") : (s.supplier || s.vessel_name || s.hbl || "Shipment");
  const docs = s.docs || [];
  return (
    <div className="animate-fade-up" data-testid="customs-detail">
      <button onClick={() => navigate("/customs")} data-testid="customs-back" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-ink"><ArrowLeft className="h-4 w-4" />All shipments</button>

      <div className="mb-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]">
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h1 className="font-display text-xl font-bold text-ink">{title}</h1>
          <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${isFcx ? "bg-[#7C3AED]/10 text-[#7C3AED]" : "bg-slate-100 text-slate-600"}`}>{s.ship_type || "FCL"}</span>
          <span className="rounded-full px-2.5 py-1 text-xs font-bold text-white" style={{ background: stColor(s.status) }}>{s.status}</span>
        </div>
        <div className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
          {s.hbl && <div><span className="text-xs text-muted-foreground">HBL</span><div className="font-semibold text-ink">{s.hbl}</div></div>}
          {s.carrier && <div><span className="text-xs text-muted-foreground">Carrier</span><div className="font-semibold text-ink">{s.carrier}</div></div>}
          {(s.pol || s.pod) && <div className="sm:col-span-2"><span className="text-xs text-muted-foreground">Route</span><div className="font-semibold text-ink">{[s.pol, s.pod].filter(Boolean).join("  →  ")}</div></div>}
        </div>
        {s.notes && <p className="mt-3 border-t border-slate-100 pt-3 text-sm text-slate-600">{s.notes}</p>}
      </div>

      {s.tracking && <TrackingPanel tracking={s.tracking} containerNo={s.container_no} title={s.supplier || s.vessel_name || s.hbl} />}

      {s.containers?.length > 0 && (
        <div className="mb-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]" data-testid="customs-containers">
          <div className="mb-3 flex items-center gap-2 font-display text-sm font-bold text-ink"><Container className="h-4 w-4 text-primary" />Containers <span className="text-xs font-normal text-muted-foreground">· {s.containers.length}</span></div>
          <div className="flex flex-wrap gap-2">
            {s.containers.map((c) => (
              <span key={c.number} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-xs font-semibold ${c.number === s.container_no ? "bg-primary/10 text-primary" : "bg-slate-100 text-slate-600"}`}>{c.number}{c.number === s.container_no && <span className="rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold text-white">tracked</span>}</span>
            ))}
          </div>
        </div>
      )}

      {s.suppliers?.length > 0 && (
        <div className="mb-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]" data-testid="customs-suppliers">
          <div className="mb-2 flex items-center gap-2 font-display text-sm font-bold text-ink"><Users className="h-4 w-4 text-[#7C3AED]" />Suppliers <span className="text-xs font-normal text-muted-foreground">· {s.suppliers.length}</span></div>
          <div className="divide-y divide-slate-100">
            {s.suppliers.map((sp) => (
              <div key={sp.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span className="font-semibold text-ink">{sp.supplier || "Supplier"}</span>
                {sp.hbl && <span className="font-mono text-xs text-muted-foreground">HBL {sp.hbl}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-4 rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]" data-testid="customs-notes-section">
        <div className="mb-3 flex items-center gap-2 font-display text-sm font-bold text-ink"><MessageSquarePlus className="h-4 w-4 text-primary" />Notes <span className="text-xs font-normal text-muted-foreground">· {(s.customs_notes || []).length}</span></div>
        {(s.customs_notes || []).length > 0 && (
          <div className="mb-4 space-y-2">
            {s.customs_notes.map((nt) => { const m = NOTE_META[nt.status] || NOTE_META.Info; const Icon = m.icon; return (
              <div key={nt.id} className="rounded-xl border border-slate-100 p-3" data-testid={`customs-note-${nt.id}`}>
                <div className="mb-1 flex flex-wrap items-center gap-2"><span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: m.color }}><Icon className="h-3 w-3" />{nt.status}</span><span className="text-[11px] text-muted-foreground">{nt.agent_name || nt.agent_email} · {new Date(nt.at).toLocaleString()}</span></div>
                <div className="text-sm text-ink">{nt.text}</div>
              </div> ); })}
          </div>
        )}
        <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
          <div className="mb-2 flex gap-1.5">
            {["Query", "Cleared", "Info"].map((st) => { const m = NOTE_META[st]; const Icon = m.icon; return (
              <button key={st} onClick={() => setNoteStatus(st)} data-testid={`note-status-${st}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition-all ${noteStatus === st ? "text-white" : "border border-slate-200 bg-white text-muted-foreground"}`} style={noteStatus === st ? { background: m.color } : {}}><Icon className="h-3 w-3" />{st}</button> ); })}
          </div>
          <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note for the TilesMate team…" className="mb-2 min-h-[64px] bg-white" data-testid="customs-note-input" />
          <div className="flex justify-end"><Button size="sm" onClick={addNote} disabled={posting} className="gap-1.5" data-testid="customs-note-submit">{posting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MessageSquarePlus className="h-3.5 w-3.5" />}Post note</Button></div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_2px_12px_rgba(35,52,90,0.04)]">
        <div className="mb-3 font-display text-sm font-bold text-ink">Documents <span className="text-xs font-normal text-muted-foreground">· {docs.length}</span></div>
        {docs.length === 0 ? <p className="rounded-xl border border-dashed border-border bg-white/60 p-6 text-center text-xs text-muted-foreground">No documents.</p>
          : <div className="divide-y divide-slate-100">
            {docs.map((d) => (
              <button key={d.id} onClick={() => viewDoc(d.id)} data-testid={`customs-doc-${d.id}`} className="flex w-full items-center justify-between gap-3 py-2.5 text-left hover:text-primary">
                <span className="flex min-w-0 items-center gap-2"><FileText className="h-4 w-4 shrink-0 text-slate-400" /><span className="truncate text-sm text-ink">{d.filename}</span></span>
                <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">{d.doc_type}</span>
              </button>
            ))}
          </div>}
      </div>
    </div>
  );
}

function ListView({ navigate }) {
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["customs-shipments"], queryFn: async () => (await customsClient.get("/customs/shipments")).data, retry: false });
  const rows = data || [];
  const term = q.trim().toLowerCase();
  const filtered = term ? rows.filter((s) => [s.supplier, s.vessel_name, s.hbl, s.container_no, s.carrier, ...(s.suppliers || []).map((x) => x.supplier)].filter(Boolean).some((v) => String(v).toLowerCase().includes(term))) : rows;
  return (
    <div className="animate-fade-up">
      <div className="mb-5">
        <h1 className="font-display text-3xl font-bold text-ink">Shipments</h1>
        <p className="mt-1 text-sm text-muted-foreground">Live status, containers, suppliers & documents for every incoming shipment.</p>
      </div>
      {isLoading ? <div className="h-40 animate-pulse rounded-2xl bg-muted/50" />
        : <>
          <ShipmentsOverview rows={rows} />
          <div className="relative mb-3">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search supplier, HBL, container…" className="pl-9" data-testid="customs-search" />
          </div>
          {filtered.length === 0 ? <div className="rounded-2xl border border-dashed border-border bg-white/60 p-12 text-center text-sm text-muted-foreground">No shipments found.</div>
            : <div className="space-y-3" data-testid="customs-shipment-list">
              {filtered.map((s) => <ShipmentRow key={s.id} s={s} onClick={() => navigate(`/customs/${s.id}`)} />)}
            </div>}
        </>}
    </div>
  );
}

export default function CustomsDashboard({ me }) {
  const { vid } = useParams();
  const navigate = useNavigate();
  return <Shell me={me}>{vid ? <DetailView vid={vid} navigate={navigate} /> : <ListView navigate={navigate} />}</Shell>;
}
